import React, { useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAdminAuth } from "../context/AdminAuthContext";
import { fetchProduct } from "../lib/api";
import AdminHeader from "../components/admin/AdminHeader";
import AdminStats from "../components/admin/AdminStats";
import AdminTabs from "../components/admin/AdminTabs";
import OrdersTab from "../components/admin/OrdersTab";
import TeamOrdersTab from "../components/admin/TeamOrdersTab";
import NewsletterTab from "../components/admin/NewsletterTab";
import MessagesTab from "../components/admin/MessagesTab";
import ReviewsTab from "../components/admin/ReviewsTab";
import ImagesTab from "../components/admin/ImagesTab";
import SiteControlTab from "../components/admin/SiteControlTab";
import CsvExportButton from "../components/admin/CsvExportButton";

const AdminDashboardPage = () => {
  const { admin, loading, logout, authClient } = useAdminAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("site-control");
  const [data, setData] = useState({
    orders: [],
    teamOrders: [],
    newsletter: [],
    messages: [],
    reviews: [],
    images: [],
  });
  const [product, setProduct] = useState(null);
  const [uploadColour, setUploadColour] = useState("");
  const [uploadGrip, setUploadGrip] = useState("");
  const [uploading, setUploading] = useState(false);

  const reload = async () => {
    if (!admin) return;
    const client = authClient();
    try {
      const [orders, teamOrders, newsletter, messages, reviews, images] = await Promise.all([
        client.get("/admin/orders"),
        client.get("/admin/team-orders"),
        client.get("/admin/newsletter"),
        client.get("/admin/messages"),
        client.get("/admin/reviews"),
        client.get("/admin/product-images"),
      ]);
      setData({
        orders: orders.data,
        teamOrders: teamOrders.data,
        newsletter: newsletter.data,
        messages: messages.data,
        reviews: reviews.data,
        images: images.data,
      });
    } catch {
      toast.error("Couldn't load admin data.");
    }
  };

  useEffect(() => {
    if (admin) {
      reload();
      fetchProduct().then(setProduct).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [admin]);

  const stats = useMemo(
    () => ({
      paidOrders: data.orders.filter((o) => o.payment_status === "paid").length,
      grossRevenue: data.orders
        .filter((o) => o.payment_status === "paid")
        .reduce((sum, o) => sum + (o.amount || 0), 0),
      teamEnquiries: data.teamOrders.length,
      subscribers: data.newsletter.length,
      messages: data.messages.length,
      reviews: data.reviews.length,
      images: data.images.length,
    }),
    [data]
  );

  if (loading) {
    return <div className="container-x py-24 text-mute">Loading admin…</div>;
  }

  if (!admin) {
    return <Navigate to="/admin/login" replace />;
  }

  const onFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!uploadColour) {
      toast.error("Pick a colourway first.");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("colourway_slug", uploadColour);
      if (uploadGrip) fd.append("grip_slug", uploadGrip);
      fd.append("file", file);
      const client = authClient();
      await client.post("/admin/product-images", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Image uploaded.");
      e.target.value = "";
      reload();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  const onDeleteImage = async (id) => {
    if (!window.confirm("Soft-delete this image?")) return;
    try {
      await authClient().delete(`/admin/product-images/${id}`);
      toast.success("Image removed.");
      reload();
    } catch {
      toast.error("Couldn't delete.");
    }
  };

  const onDeleteReview = async (id) => {
    if (!window.confirm("Delete this review?")) return;
    try {
      await authClient().delete(`/admin/reviews/${id}`);
      toast.success("Review deleted.");
      reload();
    } catch {
      toast.error("Couldn't delete.");
    }
  };

  const onLogout = () => {
    logout();
    navigate("/admin/login");
  };

  return (
    <div className="bg-chalk min-h-screen" data-testid="admin-dashboard">
      <div className="container-x py-10">
        <AdminHeader admin={admin} onReload={reload} onLogout={onLogout} />
        <AdminStats stats={stats} />
        <AdminTabs tab={tab} setTab={setTab} />

        {tab === "site-control" && <SiteControlTab authClient={authClient} />}
        {tab === "orders" && (
          <div data-testid="orders-tab-wrap">
            <div className="flex justify-end mb-3">
              <CsvExportButton
                authClient={authClient}
                endpoint="/admin/cms/export/orders.csv"
                filename="pritch-orders.csv"
                label="Export orders CSV"
              />
            </div>
            <OrdersTab orders={data.orders} />
          </div>
        )}
        {tab === "team-orders" && (
          <div data-testid="team-orders-tab-wrap">
            <div className="flex justify-end mb-3">
              <CsvExportButton
                authClient={authClient}
                endpoint="/admin/cms/export/team-orders.csv"
                filename="pritch-team-orders.csv"
                label="Export enquiries CSV"
              />
            </div>
            <TeamOrdersTab teamOrders={data.teamOrders} />
          </div>
        )}
        {tab === "newsletter" && (
          <div data-testid="newsletter-tab-wrap">
            <div className="flex justify-end mb-3">
              <CsvExportButton
                authClient={authClient}
                endpoint="/admin/cms/export/newsletter.csv"
                filename="pritch-newsletter.csv"
                label="Export subscribers CSV"
              />
            </div>
            <NewsletterTab newsletter={data.newsletter} />
          </div>
        )}
        {tab === "messages" && <MessagesTab messages={data.messages} />}
        {tab === "reviews" && (
          <ReviewsTab reviews={data.reviews} onDelete={onDeleteReview} />
        )}
        {tab === "images" && (
          <ImagesTab
            product={product}
            images={data.images}
            uploadColour={uploadColour}
            setUploadColour={setUploadColour}
            uploadGrip={uploadGrip}
            setUploadGrip={setUploadGrip}
            uploading={uploading}
            onFileChange={onFileChange}
            onDelete={onDeleteImage}
          />
        )}
      </div>
    </div>
  );
};

export default AdminDashboardPage;
